import { useEffect, useRef, useState } from "react";
import { ApiError } from "@common/api";
import { timeoutSignal } from "@common/fetch-logger";
import { cloudSaves, type CloudSaveMeta } from "./cloudSaves";
import { isSaveFileFor, saveFileName, stateSlotOf } from "./saves";
import { setSyncingRom } from "./offlineSync";
import { checksum, planSync } from "./syncPlan";
import { claimLocalSaves, loadRecords, ownsLocalSaves, saveRecords } from "./syncRecords";
import type { Emulator } from "./useEmulator";

// EmulatorJS has no save-changed callback, so its saves are only caught by
// this poll; mGBA's also upload shortly after each change. The poll is also
// how changes from other devices are noticed while a game runs.
export const SYNC_INTERVAL_MS = 60_000;
const CHANGE_DEBOUNCE_MS = 3_000;
const LEAVE_TIMEOUT_MS = 5_000;
const OTHER_ACCOUNT = "this browser's saves are still another account's, because another CloudBoy tab is open. Close it and reload to switch to this account's own.";

let flushNow: ((signal?: AbortSignal) => Promise<void>) | null = null;
// How the latest pass ended: everything settled, an upload or listing
// failed, or files are held back for a conflict with another device.
let lastPass: "synced" | "failed" | "held" = "synced";

/**
 * Uploads the open game's unsynced saves. Reads them right away, so it can
 * be called just before the game stops; the upload outlives the game.
 */
export function flushCloudSync(): Promise<void> {
  return flushNow?.() ?? Promise.resolve();
}

/**
 * flushCloudSync, reporting how it went: "off" when sync isn't running for
 * the open game (signed out, or this browser's saves are another account's).
 */
export async function syncNow(): Promise<"synced" | "failed" | "held" | "off"> {
  if (!flushNow) return "off";
  await flushNow();
  return lastPass;
}

/**
 * flushCloudSync, given a few seconds: for leaving the page. Past them its
 * requests are cancelled rather than left running behind the navigation.
 * Never rejects.
 */
export function flushCloudSyncBeforeLeaving(): Promise<void> {
  return flushNow?.(timeoutSignal(LEAVE_TIMEOUT_MS)) ?? Promise.resolve();
}

/** Settles when `signal` aborts; never, without one. */
function aborted(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) resolve();
    signal?.addEventListener("abort", () => resolve(), { once: true });
  });
}

export interface CloudSync {
  /** The open game's cloud files, as last listed; null until the first listing. */
  cloud: CloudSaveMeta[] | null;
  /** When this browser last matched the cloud, or null. */
  syncedAt: Date | null;
  error: string | null;
  /** The sign-in no longer works: the error needs a fresh sign-in, not a retry. */
  expired: boolean;
  /** The cloud holds copies from another device that this browser hasn't taken. */
  newer: { at: Date; conflict: boolean } | null;
  /** This browser holds changes the cloud lacks. */
  dirty: boolean;
  busy: boolean;
  /** Uploads every local file, replacing the cloud's. */
  push(): Promise<void>;
  /** Downloads every cloud file, replacing the local ones. */
  pull(): Promise<void>;
}

const IDLE: CloudSync = {
  cloud: null,
  syncedAt: null,
  error: null,
  expired: false,
  newer: null,
  dirty: false,
  busy: false,
  push: () => Promise.resolve(),
  pull: () => Promise.resolve(),
};

interface Snapshot {
  name: string;
  sum: number;
  bytes: Uint8Array;
}

interface Pass {
  /** Local files read ahead of time, for a flush just before the game stops. */
  snapshot?: Snapshot[];
  /** A manual upload or restore: one direction, every file. */
  force?: "push" | "pull";
  /** Cancels the pass's requests: a flush before leaving the page. */
  signal?: AbortSignal;
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : "Cloud sync failed.");
const expired = (e: unknown) => e instanceof ApiError && e.status === 401;
const newestOf = (files: CloudSaveMeta[]) => new Date(Math.max(...files.map((f) => Date.parse(f.updatedAt) || 0)));

// Keeps the open game's battery save and states in step with the cloud.
// Each pass lists the cloud and settles every file it can: on open, a copy
// from another device is restored (after a confirm when there is local
// progress to lose, which is also a conflict mid-game); changes made here
// are uploaded after they settle, on a slow poll, when the tab is hidden or
// shown, and when the browser comes back online. Skipped when signed out.
/** Away this long and coming back counts as a fresh start: a newer cloud save is offered again. */
export const RESUME_AFTER_MS = 10 * 60 * 1000;

/** How the hook asks the player something; the page supplies its dialog. */
export type Ask = (question: { title: string; message: string; action: string }) => Promise<boolean>;

export function useCloudSync(emu: Emulator, signedIn: boolean, ask: Ask): CloudSync {
  const askRef = useRef(ask);
  askRef.current = ask;
  const emuRef = useRef(emu);
  emuRef.current = emu;
  const rom = emu.engine !== null && emu.status === "ready" ? emu.current : null;
  const [state, setState] = useState<CloudSync>(IDLE);

  useEffect(() => {
    if (!signedIn || !rom) {
      setState(IDLE);
      return;
    }
    if (!ownsLocalSaves()) {
      setState({ ...IDLE, error: OTHER_ACCOUNT });
      return;
    }
    let cancelled = false;
    const openedAt = Date.now();
    const records = loadRecords(rom);
    const battery = saveFileName(rom);

    // An EmulatorJS game's states stay in the browser; one in the cloud (a
    // library upload from before that was refused) is not this game's to sync.
    const listCloud = async (signal?: AbortSignal) =>
      (await cloudSaves.list(signal)).filter((s) => (emuRef.current.engine === "ejs" ? s.name === battery : isSaveFileFor(rom, s.name)));
    const readLocal = (): Snapshot[] =>
      emuRef.current.localSaves().map((file) => ({ name: file.name, sum: checksum(file.bytes), bytes: file.bytes }));
    const update = (patch: Partial<CloudSync>) => {
      if (!cancelled) setState((s) => ({ ...s, ...patch }));
    };

    const pass = async ({ snapshot, force, signal }: Pass) => {
      // Until the player touches the game, a restart from a cloud save
      // costs nothing; after that, taking the battery save waits for them,
      // unless they've been away a while: then a newer save from another
      // device is offered again, since they may have played there.
      const untouched = emuRef.current.lastInputAt() < openedAt;
      const away = !untouched && Date.now() - emuRef.current.lastInputAt() > RESUME_AFTER_MS;
      const cloud = await listCloud(signal);
      if (cancelled && !snapshot) return;
      claimLocalSaves();
      const local = snapshot ?? readLocal();
      const localOf = (name: string) => local.find((f) => f.name === name)!;
      const cloudOf = (name: string) => cloud.find((f) => f.name === name)!;
      const plan = planSync(local, cloud, records);
      let push = plan.push;
      let pull = plan.pull;
      let conflicts = plan.conflict;
      const deleteLocal = force ? [] : plan.deleteLocal;
      const deleteCloud = force ? [] : plan.deleteCloud;
      if (force === "push") [push, pull, conflicts] = [local.map((f) => f.name), [], []];
      if (force === "pull") [push, pull, conflicts] = [[], cloud.map((f) => f.name), []];

      // A conflict whose two copies turn out identical isn't one.
      const same = async (name: string) => {
        const bytes = await cloudSaves.download(name, signal);
        if (checksum(bytes) !== localOf(name).sum) return false;
        records[name] = { sum: localOf(name).sum, at: cloudOf(name).updatedAt };
        return true;
      };
      conflicts = (await Promise.all(conflicts.map(async (name) => ((await same(name)) ? null : name)))).filter(
        (name): name is string => name !== null,
      );
      if (conflicts.length && (untouched || away) && !cancelled) {
        const when = newestOf(conflicts.map(cloudOf)).toLocaleString();
        const load = await askRef.current({
          title: "Load the newer cloud save?",
          message: `A newer save from another device is in the cloud (${when}). ${
            away ? "The game restarts from it, and progress" : "Progress"
          } here since your last sync is replaced.`,
          action: "Load it",
        });
        if (load) {
          pull = [...pull, ...conflicts];
        } else {
          push = [...push, ...conflicts];
        }
        conflicts = [];
      }
      // Back after a while with a newer battery save in the cloud and
      // nothing new here: offered too, since taking it restarts the game.
      if (away && !cancelled && pull.includes(battery) && !force) {
        const load = await askRef.current({
          title: "Load the newer cloud save?",
          message: `A newer save from another device is in the cloud (${cloudOf(battery).updatedAt ? new Date(cloudOf(battery).updatedAt).toLocaleString() : "just now"}). The game restarts from it.`,
          action: "Load it",
        });
        if (!load) pull = pull.filter((name) => name !== battery);
      }
      // Mid-game the battery save is left for the player: taking it
      // restarts the game. A flush after the game stopped can't take anything.
      const held = pull.filter((name) => cancelled || (name === battery && !untouched && !away && !force));
      pull = pull.filter((name) => !held.includes(name));

      // States first: restoring the battery save restarts the game.
      for (const name of [...pull].sort((a, b) => Number(a === battery) - Number(b === battery))) {
        const bytes = await cloudSaves.download(name, signal);
        if (cancelled) return;
        if (await emuRef.current.importCloudFile(name, bytes)) records[name] = { sum: checksum(bytes), at: cloudOf(name).updatedAt };
      }
      if (!cancelled) {
        for (const name of deleteLocal) {
          await emuRef.current.removeSlot(stateSlotOf(name)!);
          delete records[name];
        }
      }
      for (const name of plan.forget) delete records[name];

      // One file the cloud refuses doesn't hold back the others.
      let failure: unknown = null;
      for (const name of deleteCloud) {
        try {
          await cloudSaves.remove(name, signal).catch((e) => {
            if (!(e instanceof ApiError && e.status === 404)) throw e;
          });
          delete records[name];
        } catch (e) {
          failure ??= e;
        }
      }
      const pushed: string[] = [];
      for (const name of push) {
        try {
          await cloudSaves.upload(name, localOf(name).bytes, signal);
          pushed.push(name);
        } catch (e) {
          failure ??= e;
        }
      }
      let after = cloud;
      if (pushed.length || deleteCloud.length) {
        // The timestamps the cloud gave the uploads are what the next pass compares against.
        after = await listCloud(signal);
        for (const name of pushed) {
          const meta = after.find((f) => f.name === name);
          if (meta) records[name] = { sum: localOf(name).sum, at: meta.updatedAt };
        }
      }
      saveRecords(rom, records);

      const waiting = cancelled ? conflicts : [...conflicts, ...held];
      lastPass = failure ? "failed" : waiting.length ? "held" : "synced";
      update({
        cloud: after,
        error: failure ? messageOf(failure) : null,
        expired: expired(failure),
        newer: waiting.length ? { at: newestOf(waiting.map(cloudOf)), conflict: conflicts.length > 0 } : null,
        dirty: conflicts.length > 0 || pushed.length < push.length,
        ...(failure || waiting.length ? {} : { syncedAt: new Date() }),
      });
      if (force && failure) throw failure;
    };

    // Passes run one at a time; plain ones (poll, tab shown, back online)
    // asked for while one runs collapse into a single next pass.
    let chain = Promise.resolve();
    let queued = false;
    const schedule = (work: Pass = {}) => {
      const plain = !work.snapshot && !work.force;
      if (plain && queued) return chain;
      if (plain) queued = true;
      // A flush with a deadline stops waiting for the pass ahead of it
      // when the deadline passes, and is then skipped.
      const ahead = work.signal ? Promise.race([chain, aborted(work.signal)]) : chain;
      const run = ahead.then(async () => {
        if (plain) queued = false;
        if (cancelled && !work.snapshot) return;
        if (work.signal?.aborted) return;
        await pass(work);
      });
      // Still after the pass ahead, even when this one stopped waiting for it.
      const before = chain;
      chain = Promise.all([before, run.catch(() => {})]).then(() => {});
      return run.catch((e) => {
        lastPass = "failed";
        update({ error: messageOf(e), expired: expired(e) });
        if (work.force) throw e;
      });
    };
    const manual = (force: "push" | "pull") => {
      update({ busy: true });
      return schedule({ force }).finally(() => update({ busy: false }));
    };
    setState({ ...IDLE, push: () => manual("push"), pull: () => manual("pull") });

    setSyncingRom(rom);
    schedule();
    flushNow = (signal) => schedule({ snapshot: readLocal(), signal });
    const tick = () => void schedule();
    const timer = window.setInterval(tick, SYNC_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("online", tick);
    return () => {
      cancelled = true;
      flushNow = null;
      setSyncingRom(null);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("online", tick);
    };
  }, [signedIn, rom]);

  // Each change to the save files uploads once they settle.
  useEffect(() => {
    if (!emu.saveRevision) return;
    const timer = window.setTimeout(flushCloudSync, CHANGE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [emu.saveRevision]);

  return state;
}
